import { useState } from 'react'
import {
  Button,
  Chip,
  ConfirmDeleteModal,
  EmptyState,
  Input,
  Modal,
  Skeleton,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../components/ui'
import { PageHeader } from '../components/PageHeader'
import {
  useCreateWebhook,
  useDeleteWebhook,
  useEventTypes,
  useUpdateWebhook,
  useWebhookDeliveries,
  useWebhooks,
} from '../api/hooks'
import { ApiError } from '../api/client'
import type { WebhookSubscription, WebhookSubscriptionBody } from '../api/types'
import { useAuthStore } from '../stores/auth'
import { relativeTime } from '../lib/format'

function detail(err: unknown): string {
  return err instanceof ApiError ? err.detail : err instanceof Error ? err.message : String(err)
}

type DeliveryTone = 'ok' | 'danger' | 'warn' | 'muted'

function deliveryChip(status: WebhookSubscription['last_delivery_status']): { tone: DeliveryTone; label: string } {
  if (status === 'success') return { tone: 'ok', label: 'Success' }
  if (status === 'failed') return { tone: 'danger', label: 'Failed' }
  if (status === 'pending') return { tone: 'warn', label: 'Pending' }
  return { tone: 'muted', label: '—' }
}

/** Shared add/edit form. The PUT is full-replace INCLUDING the secret, and
 *  the API never returns it — an edit must re-enter it. */
function WebhookFormModal({
  open,
  title,
  initial,
  pending,
  error,
  submitLabel,
  onSubmit,
  onClose,
}: {
  open: boolean
  title: string
  initial: WebhookSubscription | null
  pending: boolean
  error: string | null
  submitLabel: string
  onSubmit: (body: WebhookSubscriptionBody) => void
  onClose: () => void
}) {
  const eventTypes = useEventTypes()
  const [url, setUrl] = useState(initial?.url ?? '')
  const [secret, setSecret] = useState('')
  const [selected, setSelected] = useState<string[]>(initial?.event_types ?? [])
  const [active, setActive] = useState(initial?.active ?? true)

  const toggleType = (key: string) =>
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]))

  return (
    <Modal open={open} title={title} onClose={onClose}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          if (!url || !secret) return
          onSubmit({ url, secret, event_types: selected, active })
        }}
      >
        <Input
          label="Endpoint URL"
          type="url"
          required
          placeholder="https://example.com/ostrel/events"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        <div className="flex flex-col gap-1.5">
          <Input
            label={initial ? 'Signing secret (re-enter)' : 'Signing secret'}
            type="password"
            required
            autoComplete="off"
            placeholder="whsec_…"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
          />
          <p className="text-[11px] leading-relaxed text-ostrel-ink-muted">
            {initial
              ? 'Write-only and never shown — editing a webhook always re-sets the secret.'
              : 'Used to sign every delivery (X-Ostrel-Signature: sha256=<hmac-sha256 hex of the raw body>).'}{' '}
            Store it somewhere safe — it cannot be retrieved later.
          </p>
        </div>
        <div className="flex flex-col gap-1.5">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
            Event types
          </p>
          <div className="max-h-44 overflow-y-auto rounded-sm border border-ostrel-line bg-ostrel-card p-2">
            {eventTypes.isPending ? (
              <Skeleton className="h-6" />
            ) : eventTypes.isError ? (
              <p className="text-xs text-ostrel-danger">{detail(eventTypes.error)}</p>
            ) : (
              <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                {eventTypes.data.map((et) => (
                  <label
                    key={et.key}
                    className="flex cursor-pointer items-center gap-2 rounded-sm px-1.5 py-1 text-xs text-ostrel-ink hover:bg-ostrel-sand"
                  >
                    <input
                      type="checkbox"
                      checked={selected.includes(et.key)}
                      onChange={() => toggleType(et.key)}
                      className="accent-ostrel-gold"
                    />
                    <span className="tnum font-mono">{et.key}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <p className="text-[11px] leading-relaxed text-ostrel-ink-muted">
            {selected.length === 0
              ? 'None selected — this webhook receives ALL event types.'
              : `${selected.length} selected — only these are delivered.`}
          </p>
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-ostrel-ink">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            className="accent-ostrel-gold"
          />
          Active — deliver events as they happen
        </label>
        {error && <p className="text-xs text-ostrel-danger">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" size="sm" disabled={pending || !url || !secret}>
            {pending ? 'Saving…' : submitLabel}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

function DeliveriesModal({ webhook, onClose }: { webhook: WebhookSubscription | null; onClose: () => void }) {
  const deliveries = useWebhookDeliveries(webhook?.unique_id ?? '')
  return (
    <Modal open={webhook !== null} title={`Deliveries — ${webhook?.url ?? ''}`} onClose={onClose}>
      {deliveries.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-8" />
          <Skeleton className="h-8" />
          <Skeleton className="h-8" />
        </div>
      ) : deliveries.isError ? (
        <EmptyState title="Could not load deliveries" hint={detail(deliveries.error)} />
      ) : deliveries.data.length === 0 ? (
        <EmptyState title="No deliveries yet" hint="Deliveries appear here once matching events are recorded." />
      ) : (
        <div className="max-h-96 overflow-y-auto rounded-sm border border-ostrel-line">
          <Table>
            <THead>
              <TR>
                <TH>When</TH>
                <TH>Event</TH>
                <TH>Status</TH>
                <TH>Attempts</TH>
                <TH>HTTP</TH>
                <TH>Error</TH>
              </TR>
            </THead>
            <TBody>
              {deliveries.data.map((d) => (
                <TR key={d.unique_id}>
                  <TD>
                    <span className="tnum text-xs text-ostrel-ink-muted" title={d.created_at}>
                      {relativeTime(d.created_at)}
                    </span>
                  </TD>
                  <TD>
                    <span className="tnum font-mono text-xs text-ostrel-ink-muted">{d.event}</span>
                  </TD>
                  <TD>
                    <Chip
                      tone={d.status === 'success' ? 'ok' : d.status === 'failed' ? 'danger' : 'warn'}
                      label={d.status}
                    />
                  </TD>
                  <TD>
                    <span className="tnum text-xs text-ostrel-ink">{d.attempts}</span>
                  </TD>
                  <TD>
                    <span className="tnum text-xs text-ostrel-ink-muted">{d.last_http_status ?? '—'}</span>
                  </TD>
                  <TD>
                    <span className="block max-w-56 truncate text-xs text-ostrel-ink-muted" title={d.last_error}>
                      {d.last_error || '—'}
                    </span>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </div>
      )}
    </Modal>
  )
}

export default function WebhooksPage() {
  const customer = useAuthStore((s) => s.customer)
  const amIAdmin = customer?.role === 'admin'

  const webhooks = useWebhooks()

  const [addOpen, setAddOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<WebhookSubscription | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<WebhookSubscription | null>(null)
  const [deliveriesTarget, setDeliveriesTarget] = useState<WebhookSubscription | null>(null)
  const [tableError, setTableError] = useState<string | null>(null)

  const create = useCreateWebhook()
  const update = useUpdateWebhook()
  const remove = useDeleteWebhook()
  const toggleActive = useUpdateWebhook()

  const closeAdd = () => {
    setAddOpen(false)
    create.reset()
  }
  const closeEdit = () => {
    setEditTarget(null)
    update.reset()
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        section="Operations"
        title="Webhooks"
        action={
          amIAdmin ? (
            <Button variant="primary" size="md" onClick={() => setAddOpen(true)}>
              + Add webhook
            </Button>
          ) : undefined
        }
      >
        <p className="mt-1 text-sm text-ostrel-ink-muted">
          Signed event delivery to your own endpoints — one POST per matching event.
        </p>
      </PageHeader>

      {webhooks.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
        </div>
      ) : webhooks.isError ? (
        <EmptyState title="Could not load webhooks" hint={detail(webhooks.error)} />
      ) : webhooks.data.length === 0 ? (
        <EmptyState
          title="No webhooks"
          hint={
            amIAdmin
              ? 'Add an outgoing webhook to push signed event payloads to your own systems.'
              : 'No outgoing webhooks are configured for this customer yet.'
          }
        />
      ) : (
        <div className="rounded-sm border border-ostrel-line bg-ostrel-card">
          <Table>
            <THead>
              <TR>
                <TH>Endpoint</TH>
                <TH>Events</TH>
                <TH>Status</TH>
                <TH>Last delivery</TH>
                <TH className="text-right">Manage</TH>
              </TR>
            </THead>
            <TBody>
              {webhooks.data.map((w) => {
                const health = deliveryChip(w.last_delivery_status)
                return (
                  <TR key={w.unique_id} className={w.active ? '' : 'opacity-60'}>
                    <TD>
                      <span className="min-w-0">
                        <span className="tnum block max-w-72 truncate font-mono text-xs text-ostrel-ink" title={w.url}>
                          {w.url}
                        </span>
                        <span className="tnum block font-mono text-[10px] text-ostrel-ink-muted">{w.unique_id}</span>
                      </span>
                    </TD>
                    <TD>
                      {w.event_types.length === 0 ? (
                        <Chip tone="info" label="All events" />
                      ) : (
                        <span className="flex max-w-72 flex-wrap gap-1">
                          {w.event_types.map((key) => (
                            <Chip key={key} tone="muted" label={key} />
                          ))}
                        </span>
                      )}
                    </TD>
                    <TD>
                      <Chip tone={w.active ? 'ok' : 'muted'} label={w.active ? 'Active' : 'Inactive'} />
                    </TD>
                    <TD>
                      <span className="flex items-center gap-1.5">
                        <Chip tone={health.tone} label={health.label} />
                        {w.last_delivery_at && (
                          <span className="tnum text-xs text-ostrel-ink-muted" title={w.last_delivery_at}>
                            {relativeTime(w.last_delivery_at)}
                          </span>
                        )}
                      </span>
                    </TD>
                    <TD className="text-right">
                      <span className="flex items-center justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setDeliveriesTarget(w)}>
                          Deliveries
                        </Button>
                        {amIAdmin && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => setEditTarget(w)}>
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={toggleActive.isPending}
                              onClick={() => {
                                setTableError(null)
                                // PUT is full-replace including the write-only
                                // secret — a plain active-flag flip is not
                                // possible without re-entering it, so route
                                // through the edit dialog with the flag flipped.
                                setEditTarget({ ...w, active: !w.active })
                              }}
                            >
                              {w.active ? 'Deactivate' : 'Activate'}
                            </Button>
                            <Button variant="danger" size="sm" onClick={() => setDeleteTarget(w)}>
                              Delete
                            </Button>
                          </>
                        )}
                      </span>
                    </TD>
                  </TR>
                )
              })}
            </TBody>
          </Table>
        </div>
      )}

      {tableError && <p className="text-xs text-ostrel-danger">{tableError}</p>}

      <div className="grid gap-3 md:grid-cols-3">
        {[
          [
            'Signed payloads',
            'Every delivery is a JSON POST signed with your secret: X-Ostrel-Signature carries sha256=<HMAC-SHA256 hex of the raw body>, X-Ostrel-Event the event type. Always verify before processing.',
          ],
          ['Event filter', 'Pick the event types to receive — or pick none to subscribe to all of them.'],
          ['Retries', 'Network errors and 5xx responses retry up to 3 times (30s/60s/120s). 3xx/4xx answers are treated as final failures. Check the Deliveries view for the per-attempt log.'],
        ].map(([title, text]) => (
          <div key={title} className="rounded-sm border border-ostrel-line bg-ostrel-card p-3.5">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-gold">{title}</p>
            <p className="mt-1.5 text-[11px] leading-relaxed text-ostrel-ink-muted">{text}</p>
          </div>
        ))}
      </div>

      {addOpen && (
        <WebhookFormModal
          open={addOpen}
          title="Add webhook"
          initial={null}
          pending={create.isPending}
          error={create.isError ? detail(create.error) : null}
          submitLabel="Create webhook"
          onSubmit={(body) => create.mutate(body, { onSuccess: closeAdd })}
          onClose={closeAdd}
        />
      )}

      {editTarget && (
        <WebhookFormModal
          key={editTarget.unique_id}
          open
          title={`Edit webhook — ${editTarget.unique_id}`}
          initial={editTarget}
          pending={update.isPending}
          error={update.isError ? detail(update.error) : null}
          submitLabel="Save webhook"
          onSubmit={(body) => update.mutate({ uid: editTarget.unique_id, body }, { onSuccess: closeEdit })}
          onClose={closeEdit}
        />
      )}

      <ConfirmDeleteModal
        open={deleteTarget !== null}
        title="Delete webhook"
        objectName={deleteTarget?.url ?? ''}
        expected={deleteTarget?.unique_id ?? ''}
        pending={remove.isPending}
        error={remove.isError ? detail(remove.error) : null}
        confirmLabel={remove.isPending ? 'Deleting…' : 'Delete'}
        onConfirm={() => deleteTarget && remove.mutate(deleteTarget.unique_id, { onSuccess: () => setDeleteTarget(null) })}
        onClose={() => setDeleteTarget(null)}
      >
        <p>
          Delete the webhook to <span className="tnum font-mono text-xs">{deleteTarget?.url}</span>? Event
          delivery stops immediately; the delivery log is kept.
        </p>
      </ConfirmDeleteModal>

      <DeliveriesModal webhook={deliveriesTarget} onClose={() => setDeliveriesTarget(null)} />
    </div>
  )
}
